export const enum SearchApproaches {
    Auto = "auto",
    Text = "text",
    Aggregator = "aggregator",
}

import type { CitationTarget, SourceContext, SourceSnapshot } from "./sourceContext"

export const enum ModelVersions {
    PRO = "pro",
    LITE = "lite",
}

export const enum TokensUsageModes {
    Standard = "standard",
    Increased = "increased",
    Highest = "highest",
}

export const enum ImproveSearchQueryModes {
    Always = "Always",
    Manual = "Manual",
    Disabled = "Disabled",
}

export const enum PreselectContextFilesModes {
    Enabled = "Enabled",
    Disabled = "Disabled",
}

export const enum UserTypes {
    Beginner = "beginner",
    Expert = "expert",
}

export const enum AssistantTypes {
    BASIC = "basic",
    DOC_ASSISTANT = "doc_assistant",
    INTERNET_SEARCH = "internet_search",
}

export const enum InteractionTypes {
    ANSWER = "answer",
    CLARIFICATION_REQUEST = "clarification_request",
}

export type AskRequestOverrides = {
    temperature?: number
    answer_generation_prompt_template?: string
    cognitive_search_query_generation_prompt_template?: string
    preselect_context_files_mode?: PreselectContextFilesModes
    isChatGPT4?: boolean
    assistant_type?: AssistantTypes
    search_approach: SearchApproaches
    docs_preselect_search_threshold?: number
    model_provider: string
    selected_model?: string
    tokens_usage_mode: TokensUsageModes
    reasoning_enabled?: boolean
    suggest_followup_questions?: boolean
}

export type AttachedImage = {
    attachment_id?: string | null
    blob_name: string
    mime_type: string
    original_name?: string | null
    size?: number | null
    sas_url?: string | null
    upload_phase?: "reading" | "ready" | "uploading" | "accepted" | "failed" | "cancelled" | "unknown"
}

export type AttachedFile = {
    attachment_id?: string | null
    blob_name: string
    mime_type: string
    original_name?: string | null
    size?: number | null
    /** Truncated extracted text; persisted for LLM replay (workflow + chat). */
    extracted_text?: string | null
    sas_url?: string | null
    extraction?: {
        version: 1
        format: "ifc"
        status: "ready" | "partial" | "failed"
        reason?: string | null
        schema_identifier?: string | null
        inventory_complete: boolean
        details_complete: boolean
        property_scope: "bounded_exported_examples" | "unavailable"
        products_scanned: number
        products_shown: number
    } | null
    upload_phase?: "reading" | "ready" | "uploading" | "accepted" | "failed" | "cancelled" | "unknown"
    upload_reason?: string | null
}

export type VisualEvidenceCategory =
    | "bathtub"
    | "shower"
    | "wc"
    | "washbasin"
    | "kitchen_sink"
    | "lift"
    | "installation_shaft"
    | "tree_protected_felling"
    | "tree_unprotected_felling"

export type VisualInspectionProvenance = {
    schema_version: "visual-inspection/1"
    phase: "same_view" | "different_view" | "not_completed" | "not_run"
    outcome: "completed" | "timeout" | "invalid_response" | "provider_failure" | "budget_unavailable" | "not_run"
    answer_view_hash?: string | null
    inspected_view_hash?: string | null
    observation_status?: VisualEvidence["status"] | null
    observation_label?: string | null
}

export type VisualEvidence = {
    evidence_id: string
    source_kind: "document_figure" | "document_page" | "chat_attachment" | "chat_page"
    source_id: string
    source_revision?: string | null
    content_hash?: string | null
    file_id?: string | null
    chat_id?: string | null
    blob_name?: string | null
    original_name?: string | null
    page_number?: number | null
    region: {
        x1: number
        y1: number
        x2: number
        y2: number
        label?: string | null
        confidence?: number | null
    }
    role: "object" | "value" | "dimension" | "legend" | "context"
    status: "confirmed" | "approximate" | "unreadable" | "ambiguous" | "unavailable"
    label?: string | null
    claim_id?: string | null
    parent_evidence_id?: string | null
    category?: VisualEvidenceCategory | null
    display_number?: number | null
    inspection?: VisualInspectionProvenance | null
}

export type VisualClaim = {
    claim_id: string
    evidence_ids: string[]
    text: string
}

export type History = {
    is_deleted?: boolean
    _id?: string
    /** Safe server-owned completion state; absent/null for legacy or unobserved turns. */
    generation_state?: "pending" | "succeeded" | "failed" | "cancelled" | null
    user: string
    bot: {
        diagnostics?: import("./diagnostics").ChatDiagnostics | null
        result_blocks?: import("./queryResults").ResultBlock[]
        answer: string
        /** Chain-of-thought / extended thinking from reasoning models (o1, o3, etc.) */
        reasoning?: string | null
        data_points?: string[]
        thoughts?: string
        context_files_id?: string[]
        real_time_connectors?: string[]
        was_interrupted: boolean
        routing_error_code?: string | null
        generation_error?: "generation_failed" | null
        tokens_used?: {
            prompt_tokens?: number
            completion_tokens?: number
            total_tokens?: number
        }
        settings?: {
            search_approach?: SearchApproaches
            overrides?: AskRequestOverrides
            context_files_id?: string[]
            context_folders?: string[]
            source_context?: import("./sourceContext").SourceContext | null
            submitted_request_v1?: {
                context_files_id: string[]
                context_folders: string[]
                source_context?: import("./sourceContext").SourceContext | null
            } | null
            tokens_used?: number
        }
        /** Type of agent interaction — "answer" (default) or "clarification_request" */
        interaction_type?: InteractionTypes
        /** Quick-reply options when interaction_type is clarification_request */
        clarification_options?: string[]
        /** Selection mode for clarification — "single" (radio) or "multi" (checkboxes) */
        clarification_selection_mode?: "single" | "multi" | null
        visual_evidence?: VisualEvidence[] | null
        visual_claims?: VisualClaim[] | null
        connector_connect?: ConnectorConnectPrompt | null
        source_usage?: Array<{
            source: import("./sourceContext").SourceRef
            status: "selected" | "materialized" | "used" | "cited" | "unavailable" | "omitted"
            selected_binding_identity?: string | null
            view_ids?: string[]
            reason?: string | null
        }> | null
        citation_targets?: CitationTarget[] | null
    }
    created_time_utc: any
    attached_images?: AttachedImage[] | null
    attached_files?: AttachedFile[] | null
    id?: string
    hide_on_ui?: boolean
    hide_question_on_ui?: boolean
    source_snapshot?: SourceSnapshot | null
}

export type ConnectorConnectPrompt = {
    service: string
    service_name: string
    request_id?: string
    expires_at?: string
    reconnect?: boolean
    resume_after_connect?: boolean
}

export type AskResponse = {
    result_blocks?: import("./queryResults").ResultBlock[]
    /** Knowledge files used by a legacy turn; a saved source snapshot supersedes this list. */
    context_files_id?: string[]
    cited_figures: any
    visual_evidence?: VisualEvidence[] | null
    visual_claims?: VisualClaim[] | null
    was_interrupted: boolean
    routing_error_code?: string | null
    generation_error?: "generation_failed" | null
    title: string
    answer: string
    thoughts: any
    data_points: string[]
    is_pin: boolean
    history: History[]
    detail?: string
    tokens_used: {
        prompt_tokens: number
        completion_tokens: number
        total_tokens: number
    }
    _id?: string
    chat_id?: string
    execution_time?: number
    settings: any
    project_id?: string
    is_from_internal_knowledge?: boolean
    created_by_user_id?: string | null
    created_by_email?: string | null
    is_owner?: boolean | null
    connector_connect?: ConnectorConnectPrompt | null
    cad_references?: Array<{
        reference_id: string
        file_id: string
        generation: string
        entity_ids: string[]
        label: string
        scope: "entities" | "drawing"
        query?: {
            collection: "entities" | "blocks"
            operation: "list" | "count" | "group_count"
        }
    }> | null
    source_usage?: Array<{
        source: import("./sourceContext").SourceRef
        status: "selected" | "materialized" | "used" | "cited" | "unavailable" | "omitted"
        selected_binding_identity?: string | null
        view_ids?: string[]
        reason?: string | null
    }> | null
    citation_targets?: CitationTarget[] | null
}

export type SqlScopeSelection = {
    kind: "fresh_source" | "saved_result" | "mixed"
    source_ids?: string[]
    result_ids?: string[]
}

export type ChatRequest = {
    continuation_parent_history_id?: string
    capabilities?: string[]
    submission_id?: string
    attachment_ids?: import("@/utils/chatAttachmentSubmission").AttachmentIds
    user_question: string
    overrides?: AskRequestOverrides
    context_files_id: string[]
    context_folders?: string[]
    chat_id?: string
    should_be_pinned?: boolean
    user_type: UserTypes
    project_id?: string
    images?: string[]
    image_names?: string[]
    existing_images?: AttachedImage[]
    files?: string[]
    file_names?: string[]
    existing_files?: AttachedFile[]
    timezone?: string
    source_context?: SourceContext
    sql_scope?: SqlScopeSelection
}
